<script setup>
import { cloneDeep, difference, isEmpty, isNil, xor } from 'lodash'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import HomepageUsersApiHandler from '@/api/homepage-users'
import { BUTTON_SIZES_NEXT, BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { ASSIGNEE_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { useHomepageSidebarFilteredOutTooltip } from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { NEW_HP_FILTERS_KEYS } from '@/utils/okr-elements/filters'
import { stringOrNullProp } from '@/utils/prop-validators'
import { useLoadingState } from '@/utils/use-loading-state'

import HomepageSidebarFilterEmptyState from '@/components/home-page/HomepageSidebarFilterEmptyState'
import HomepageSidebarFilterItem from '@/components/home-page/HomepageSidebarFilterItem'
import HomepageSidebarFilterLayout from '@/components/home-page/HomepageSidebarFilterLayout'
import HomepageSidebarFilterList from '@/components/home-page/HomepageSidebarFilterList'
import HomepageSidebarPinnedList from '@/components/home-page/HomepageSidebarPinnedList'
import HomepageSidebarSegmentTitle from '@/components/home-page/HomepageSidebarSegmentTitle'
import HomepageUsersFilterLoader from '@/components/home-page/HomepageUsersFilterLoader'
import {
  SIDEBAR_UNPIN_BUTTON_TEST_ID,
  SIDEBAR_USERS_LIST_TEST_ID
} from '@/components/home-page/jest-helpers'
import ObjectivesEmptyHero from '@/components/objectives/ObjectivesEmptyHero'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppIcon from '@/components/ui/AppIcon/AppIcon'

defineOptions({
  name: 'HomepageUsersFilter'
})

const homePageState = inject(homePageStateInjectionKey)
const homePageFilters = homePageState.filterValues

//#region Workspace filtering
const selectedWorkspaceIds = computed(
  () => homePageState.filterValues.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]
)

const isUserFilteredOut = (user = {}) => {
  if (isEmpty(user)) {
    return false
  }

  const userWorkspaceIds = user[ASSIGNEE_ENTITY_KEYS.WORKSPACE_IDS] ?? []

  return !userWorkspaceIds.some(id => selectedWorkspaceIds.value.includes(id))
}

const { filteredOutItemConfig } = useHomepageSidebarFilteredOutTooltip(
  'homepage.user_not_in_ws',
  () => selectedWorkspaceIds.value.length
)

//#endregion

const api = new HomepageUsersApiHandler()

const store = useStore()
const { t } = useI18n()

const { isLoading, toggleLoading } = useLoadingState()

//#region Membership mark
// only the user who views Home gets the mark on the Users tab
const currentUserId = computed(() => store.state.system.userData?.userAccountId ?? null)

const getUserMemberTooltip = (user = {}) => {
  return user[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID] === currentUserId.value
    ? t('homepage.membership_mark.user')
    : null
}
//#endregion

//#region Users selection
const selectedUserId = defineModel({
  required: true,
  validator: v => stringOrNullProp(v)
})

const usersList = ref([])

// the backend returns a limited amount of users, so the selected one is requested explicitly
const loadUsers = async accountId => {
  const { users } = await api.getUsersForFilter({
    requiredAccountIds: accountId ? [accountId] : null,
    includeCurrentUser: true
  })

  usersList.value = users
}

const loadInitialUsers = async () => {
  toggleLoading(true)
  try {
    await loadUsers(selectedUserId.value)
  } catch (error) {
    handleError({ error })
  } finally {
    toggleLoading(false)
  }
}

const selectUser = accountId => {
  selectedUserId.value = accountId

  if (homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP]) {
    homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP] = null
  }
}

// the user may be outside the loaded list, so the list is reloaded with them included
const selectUserWithReload = async accountId => {
  toggleLoading(true)
  try {
    await loadUsers(accountId)
    selectUser(accountId)
  } catch (error) {
    handleError({ error })
  } finally {
    toggleLoading(false)
  }
}
//#endregion

//#region Search
const searchString = ref('')

const searchResults = ref(null)

const searchUserByName = async (value = '') => {
  try {
    const { users } = await api.getUsersByName(value)

    // the input may have changed or been cleared while the request was in flight
    if (value !== searchString.value) {
      return
    }

    searchResults.value = users
  } catch (error) {
    handleError({ error })
  }
}

const clearSearchResults = () => (searchResults.value = null)

watch(searchString, async newValue => {
  if (newValue) {
    await searchUserByName(newValue)
  } else {
    clearSearchResults()
  }
})

const selectFoundedUser = accountId => {
  searchString.value = ''
  selectUserWithReload(accountId)
}
//#endregion

//#region Pinned users
const pinnedUserIds = computed(() => {
  return store.getters['system/homepagePinnedUsers']
})

const pinnedUsers = ref([])

const { isLoading: isPinnedUsersLoading, toggleLoading: togglePinnedUsersLoading } =
  useLoadingState()

const createPinnedUsersList = async () => {
  if (isEmpty(pinnedUserIds.value)) return

  togglePinnedUsersLoading(true)
  try {
    pinnedUsers.value = await api.getPinnedUsersData(pinnedUserIds.value)
  } catch (error) {
    handleError({ error })
  } finally {
    togglePinnedUsersLoading(false)
  }
}

const unpinUser = accountId => {
  store.dispatch('system/togglePinStatusForUser', {
    accountId
  })
}

const { isLoading: isPinsOrderUpdating, toggleLoading: togglePinsOrderUpdating } = useLoadingState()

const onPinnedUsersReorder = async (users = []) => {
  const snapshot = cloneDeep(pinnedUsers.value)
  const accountIds = users.map(user => user[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID])

  pinnedUsers.value = users

  togglePinsOrderUpdating(true)
  try {
    const isSaved = await store.dispatch('system/updatePinnedUsersOrder', { accountIds })

    if (!isSaved) {
      pinnedUsers.value = snapshot
    }
  } finally {
    togglePinsOrderUpdating(false)
  }
}

watch(
  pinnedUserIds,
  async (newValue, oldValue) => {
    if (isEmpty(newValue)) {
      pinnedUsers.value = []
      return
    }

    if (!isNil(oldValue) && !isNil(newValue) && newValue.length < oldValue.length) {
      const diff = difference(oldValue, newValue)

      pinnedUsers.value = pinnedUsers.value.filter(
        user => !diff.includes(user[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID])
      )
      return
    }

    // the same ids in a new order: the list is already reordered locally, nothing to fetch
    if (!isNil(oldValue) && isEmpty(xor(newValue, oldValue))) {
      return
    }

    await createPinnedUsersList()
  },
  { immediate: true, deep: true }
)
//#endregion

onMounted(() => {
  loadInitialUsers()
})
</script>

<template>
  <HomepageSidebarFilterLayout v-model="searchString" :search-results="searchResults">
    <template #no-results>
      <HomepageSidebarFilterEmptyState>
        <template #hero>
          <ObjectivesEmptyHero />
        </template>
        {{ t('global_groups.no_users_found') }}
      </HomepageSidebarFilterEmptyState>
    </template>

    <template #search-results>
      <div class="hpuf-UsersNav_List">
        <HomepageSidebarFilterItem
          v-for="item in searchResults"
          :key="item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID]"
          v-tippy="{
            ...filteredOutItemConfig,
            content: isUserFilteredOut(item) ? filteredOutItemConfig.content : null
          }"
          :filtered-out="isUserFilteredOut(item)"
          :item="item"
          :item-label="ASSIGNEE_ENTITY_KEYS.NAME"
          :member-tooltip="getUserMemberTooltip(item)"
          :selected="item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID] === selectedUserId"
          class="hpuf-UsersNav_Item"
          @click="selectFoundedUser(item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID])"
        />
      </div>
    </template>

    <HomepageSidebarPinnedList
      :disabled="isPinsOrderUpdating || isPinnedUsersLoading || pinnedUsers.length < 2"
      :item-key="ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID"
      :items="pinnedUsers"
      @update:items="onPinnedUsersReorder"
    >
      <template #item="{ item }">
        <HomepageSidebarFilterItem
          v-tippy="{
            ...filteredOutItemConfig,
            content: isUserFilteredOut(item) ? filteredOutItemConfig.content : null
          }"
          :filtered-out="isUserFilteredOut(item)"
          :item="item"
          :item-label="ASSIGNEE_ENTITY_KEYS.NAME"
          :member-tooltip="getUserMemberTooltip(item)"
          :selected="item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID] === selectedUserId"
          class="hpuf-UsersNav_Item"
          pinned
          @click="selectUserWithReload(item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID])"
        >
          <template #actions>
            <AppButtonNext
              v-tippy="{
                content: t('action.unpin'),
                placement: 'top'
              }"
              :data-auto-testid="SIDEBAR_UNPIN_BUTTON_TEST_ID"
              :data-testid="SIDEBAR_UNPIN_BUTTON_TEST_ID"
              :disabled="isPinsOrderUpdating"
              :size="BUTTON_SIZES_NEXT.XS"
              :variant="BUTTON_VARIANTS.LINK"
              class="hpuf-UnpinButton"
              icon-before="unpin"
              @click.stop="unpinUser(item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID])"
            />
          </template>
        </HomepageSidebarFilterItem>
      </template>
    </HomepageSidebarPinnedList>

    <HomepageSidebarSegmentTitle>{{ t('common.users') }}</HomepageSidebarSegmentTitle>

    <HomepageSidebarFilterList
      :data-auto-testid="SIDEBAR_USERS_LIST_TEST_ID"
      :data-testid="SIDEBAR_USERS_LIST_TEST_ID"
      :items="usersList"
      :loading="isLoading"
    >
      <template #loading>
        <HomepageUsersFilterLoader />
      </template>

      <template #no-results>
        <HomepageSidebarFilterEmptyState>
          <template #hero>
            <AppIcon height="64" icon-name="no-users-hero" width="71" />
          </template>

          {{ t('global_groups.no_users_found') }}
        </HomepageSidebarFilterEmptyState>
      </template>

      <template #item="{ item }">
        <HomepageSidebarFilterItem
          v-tippy="{
            ...filteredOutItemConfig,
            content: isUserFilteredOut(item) ? filteredOutItemConfig.content : null
          }"
          :filtered-out="isUserFilteredOut(item)"
          :item="item"
          :item-label="ASSIGNEE_ENTITY_KEYS.NAME"
          :member-tooltip="getUserMemberTooltip(item)"
          :selected="item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID] === selectedUserId"
          class="hpuf-UsersNav_Item"
          @click="selectUser(item[ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID])"
        />
      </template>
    </HomepageSidebarFilterList>
  </HomepageSidebarFilterLayout>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/homepage-mixins';

.hpuf-UsersNav_List {
  @include homepage-sidebar-list;
}

.hpuf-UsersNav_Item {
  @include homepage-sidebar-list-item;
}

.hpuf-UnpinButton {
  --abn-color: #{$dark-2};
}
</style>
